<template>
  <BuildingSectionCard
    title="Pet policy"
    tone="secondary"
    :hint="unitDefaultHint"
    :form="PropertyPetPolicyForm"
    :has-info="Boolean(policy)"
    :placeholder="{
      title: 'No default pet policy yet',
      subtitle: 'New units start with pets unspecified until you set a default.',
      buttonText: 'Add pet policy',
    }"
  >
    <template v-if="policy" #subtitle>
      <DefaultDriftLine :building-id="building.id" :differing-ids="building.defaultsDrift?.petPolicy" :units="units" />
    </template>

    <PropertyDetailsCardRows v-if="policy">
      <PropertyDetailsCardInfoItem label="Pets allowed" icon="paw" :value="petsAllowed" />
      <template v-if="policy.allowsPets">
        <PropertyDetailsCardInfoItem v-if="policy.maxPets" label="Max pets" icon="hash-01" :value="policy.maxPets" />
        <PropertyDetailsCardInfoItem
          v-if="policy.petRent"
          label="Pet rent"
          icon="repeat-01"
          :value="`$${policy.petRent} per month`"
        />
        <PropertyDetailsCardInfoItem
          v-if="policy.petDeposit"
          label="Pet deposit"
          icon="bank-note-01"
          :value="`$${policy.petDeposit}`"
        />
        <PropertyDetailsCardInfoItem
          v-if="policy.petFee"
          label="One-time pet fee"
          icon="coins-01"
          :value="`$${policy.petFee}`"
        />
      </template>
    </PropertyDetailsCardRows>
  </BuildingSectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { BuildingDto, PropertyListItemDto, PropertyPetPolicyForm } from '@tallo/investor/property';
import { PetPolicy, PropertyDetailsCardInfoItem, PropertyDetailsCardRows } from '@tallo/property';

import BuildingSectionCard from '../../../components/building-section-card.vue';
import DefaultDriftLine from '../default-drift-line.vue';
import { unitDefaultHint } from './unit-default.const';

/** The pet policy a new unit starts with, and how many units still keep it */
const props = defineProps<{ building: BuildingDto; units: PropertyListItemDto[] }>();

const policy = computed(() => props.building.petPolicy);

// "Cats, small dogs": the kinds let in, as one phrase
const petsAllowed = computed(() => {
  if (!policy.value?.allowsPets) {
    return 'No pets';
  }

  const kinds = [
    [policy.value.catsPolicy, 'cats'],
    [policy.value.smallDogsPolicy, 'small dogs'],
    [policy.value.largeDogsPolicy, 'large dogs'],
    [policy.value.otherPetsPolicy, 'other pets'],
  ]
    .filter(([rule]) => rule === PetPolicy.Allowed)
    .map(([, kind]) => kind)
    .join(', ');

  return kinds ? kinds.charAt(0).toUpperCase() + kinds.slice(1) : 'Yes';
});
</script>
